/**
 * Firestore för Affärer/Pipeline-flödet: skedesbyten med historik, offerter
 * per lead och bokade möten. Logiken som räknar på datan ligger i
 * pipelineFlode.js.
 *
 * Datamodell på crmLeads/{id} (additivt, inget gammalt fält tas bort):
 *   skedeSedan      Timestamp – när leadet kom in i nuvarande skede
 *   skedeshistorik  [{ fran, till, tid, av }] – fran är null när leadet skapas
 *   storlek         'S' | 'M' | 'L' | null
 *   nastaSteg       fritext som hör till nextFollowUp (frivillig)
 *
 * Flytt till Vilande sätter nextFollowUp till idag + 6 månader (nastaSteg
 * 'Återkontakt'), om leadet inte redan har en senare uppföljning.
 *
 * Underkollektion crmLeads/{id}/offerter/{offertId}:
 *   { datum: 'YYYY-MM-DD', namn, beloppKSEK|null, fil|null, status, skapadAv, skapadAt }
 *   fil = { name, url, storagePath, size, mimeType }
 */
import {
  collection, collectionGroup, doc, getDocs, addDoc, updateDoc, deleteDoc,
  query, where, arrayUnion, Timestamp, serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { normaliseraSkede, toMillis, vilandeAterkontakt, VILANDE_NASTA_STEG } from './pipelineFlode';
import { synkaPilotPaminnelser } from './pilotPaminnelser';

// ── Skede ───────────────────────────────────────────────────────────────────

/**
 * Återkontaktsfälten när ett lead flyttas till Vilande, annars {}.
 * `extra` går före leadet, så att ett datum som sparas i samma uppdatering
 * (t.ex. från redigeringsformuläret) räknas som leadets uppföljning.
 */
function vilandeFalt(lead, nyttSkede, extra = {}) {
  if (nyttSkede !== 'dormant' || lead?.status === 'dormant') return {};
  const nuvarande = 'nextFollowUp' in extra ? extra.nextFollowUp : lead?.nextFollowUp;
  const datum = vilandeAterkontakt(nuvarande);
  if (!datum) return {};
  return { nextFollowUp: datum, nextFollowUpTime: '', nextFollowUpStatus: '', nastaSteg: VILANDE_NASTA_STEG };
}

/** Fälten ett nytt lead får när det skapas. */
export function nyttLeadSkedesfalt(status, uid) {
  const nu = Timestamp.now();
  return {
    skedeSedan: nu,
    skedeshistorik: [{ fran: null, till: status, tid: nu, av: uid || '' }],
  };
}

/**
 * Fälten ett skedesbyte skriver, för den som bygger en egen batch.
 * Tomt objekt om skedet i praktiken är detsamma (sandbox ↔ demo).
 */
export function skedesbyteFalt(lead, nyttSkede, uid) {
  const fran = lead?.status ?? null;
  if (normaliseraSkede(fran) === normaliseraSkede(nyttSkede)) return { status: nyttSkede };
  const nu = Timestamp.now();
  return {
    ...vilandeFalt(lead, nyttSkede),
    status: nyttSkede,
    skedeSedan: nu,
    skedeshistorik: arrayUnion({ fran, till: nyttSkede, tid: nu, av: uid || '' }),
  };
}

/**
 * Byter skede och loggar bytet. `extra` skrivs i samma uppdatering.
 * Flytt till Vilande sätter återkontakt, se vilandeFalt.
 *
 * Byte mellan 'sandbox' och 'demo' är samma skede: status skrivs, men
 * skedeSedan och historiken rörs inte.
 *
 * Returnerar det som ändrats, för att uppdatera lokalt state.
 */
export async function bytSkede(leadId, lead, nyttSkede, uid, extra = {}) {
  const fran = lead?.status ?? null;
  const nu = Timestamp.now();
  const { skedeshistorik: _h, skedeSedan: _s, ...rest } = extra;
  const base = {
    ...rest, ...vilandeFalt(lead, nyttSkede, rest),
    status: nyttSkede, updatedAt: rest.updatedAt || new Date(),
  };

  if (normaliseraSkede(fran) === normaliseraSkede(nyttSkede)) {
    await updateDoc(doc(db, 'crmLeads', leadId), base);
    return base;
  }

  const post = { fran, till: nyttSkede, tid: nu, av: uid || '' };
  await updateDoc(doc(db, 'crmLeads', leadId), {
    ...base,
    skedeSedan: nu,
    skedeshistorik: arrayUnion(post),
  });
  // Till Pilot: påminnelser till ägaren utifrån kampanjofferten (i bakgrunden;
  // utan Flatbox-behörighet går offerterna inte att läsa och inget skapas)
  if (nyttSkede === 'pilot') {
    synkaPilotPaminnelser(leadId).catch(e => console.warn('Pilotpåminnelser kunde inte skapas', e));
  }
  return {
    ...base,
    skedeSedan: nu,
    skedeshistorik: [...(lead?.skedeshistorik || []), post],
  };
}

/** Nytt återkontaktsdatum ('YYYY-MM-DD') från bekräftelsen efter flytt till Vilande. */
export async function sattAterkontakt(leadId, datum) {
  const patch = { nextFollowUp: datum, nextFollowUpTime: '', nextFollowUpStatus: '', updatedAt: new Date() };
  await updateDoc(doc(db, 'crmLeads', leadId), patch);
  return patch;
}

// ── Offerter ────────────────────────────────────────────────────────────────

function offertFranDoc(d) {
  return { id: d.id, leadId: d.ref.parent.parent.id, ...d.data() };
}

export async function laddaOfferter(leadId) {
  const snap = await getDocs(collection(db, 'crmLeads', leadId, 'offerter'));
  return snap.docs.map(offertFranDoc);
}

/**
 * Alla offerter över alla leads, med leadId. Kräver regeln för
 * collection group i firestore.rules. Ger [] om frågan nekas, så att
 * pipelinesidan fungerar även innan reglerna är deployade.
 */
export async function laddaAllaOfferter() {
  try {
    const snap = await getDocs(collectionGroup(db, 'offerter'));
    return snap.docs
      .filter(d => d.ref.parent.parent?.parent?.id === 'crmLeads')
      .map(offertFranDoc);
  } catch (e) {
    console.warn('Kunde inte läsa offerter (collection group)', e);
    return [];
  }
}

function laddaUppOffertfil(leadId, file, onProgress) {
  return new Promise((resolve, reject) => {
    // Samma mapp och djup som leadets bilagor, så att samma lagringsregler gäller.
    const path = `leadAttachments/${leadId}/offert_${Date.now()}_${file.name}`;
    const fileRef = storageRef(storage, path);
    const task = uploadBytesResumable(fileRef, file);
    task.on('state_changed',
      snap => onProgress?.(snap.bytesTransferred / snap.totalBytes),
      reject,
      async () => {
        const url = await getDownloadURL(fileRef);
        resolve({ name: file.name, url, storagePath: path, size: file.size, mimeType: file.type });
      });
  });
}

export async function skapaOffert(leadId, { datum, namn, beloppKSEK, status, file }, uid, onProgress) {
  const fil = file ? await laddaUppOffertfil(leadId, file, onProgress) : null;
  const belopp = beloppKSEK === '' || beloppKSEK == null ? null : Number(beloppKSEK);
  const data = {
    datum,
    namn: (namn || '').trim(),
    beloppKSEK: Number.isFinite(belopp) ? belopp : null,
    fil,
    status: status || 'skickad',
    skapadAv: uid || '',
    skapadAt: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, 'crmLeads', leadId, 'offerter'), data);
  return { ...data, id: ref.id, leadId, skapadAt: new Date() };
}

export async function uppdateraOffertStatus(leadId, offertId, status) {
  await updateDoc(doc(db, 'crmLeads', leadId, 'offerter', offertId), { status });
}

export async function raderaOffert(leadId, offert) {
  if (offert.fil?.storagePath) {
    try { await deleteObject(storageRef(storage, offert.fil.storagePath)); } catch (_) {}
  }
  await deleteDoc(doc(db, 'crmLeads', leadId, 'offerter', offert.id));
}

// ── Möten ───────────────────────────────────────────────────────────────────

/**
 * Mötesaktiviteter för leads som ändrats sedan `sedanMs`. Varje bokning,
 * snabblogg och aktivitet sätter leadets updatedAt, så leads som inte rörts
 * sedan dess kan inte ha möten registrerade i perioden – de hoppas över.
 */
export async function laddaMoten(leads, sedanMs) {
  const aktuella = leads.filter(l => (toMillis(l.updatedAt) ?? 0) >= sedanMs);
  const svar = await Promise.all(aktuella.map(l =>
    getDocs(query(collection(db, 'crmLeads', l.id, 'activities'), where('type', '==', 'meeting')))
      .then(snap => snap.docs.map(d => ({ id: d.id, leadId: l.id, ...d.data() })))
      .catch(() => [])
  ));
  return svar.flat();
}
